import React, { forwardRef, useId } from 'react';
import './TextField.less';

/**
 * TextField — labelled input for dashboard forms. Sibling of the auth
 * FormField, but themed via the shared design tokens instead of the
 * auth-specific palette. Same a11y semantics: label bound to input,
 * error announced via aria-live.
 */
const TextField = forwardRef(function TextField(
  {
    label,
    hint,
    error,
    name,
    type = 'text',
    value,
    onChange,
    onBlur,
    autoComplete,
    inputMode,
    placeholder,
    required,
    disabled,
    readOnly,
    errorRole,
    ...rest
  },
  ref,
) {
  const uid = useId();
  const inputId = `${uid}-${name || 'field'}`;
  const errorId = error ? `${inputId}-err` : undefined;
  const hintId = hint ? `${inputId}-hint` : undefined;

  return (
    <div className="gc-field">
      {label && (
        <label htmlFor={inputId} className="gc-field-label">
          {label}
          {required && <span className="gc-field-required" aria-hidden="true">*</span>}
        </label>
      )}
      <input
        id={inputId}
        ref={ref}
        type={type}
        name={name}
        value={value ?? ''}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={!!error || undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        className={`gc-field-input${error ? ' is-invalid' : ''}`}
        {...rest}
      />
      {hint && !error && (
        <p id={hintId} className="gc-field-hint">{hint}</p>
      )}
      {error && (
        <p id={errorId} className="gc-field-error" role={errorRole} aria-live={errorRole ? undefined : 'polite'}>{error}</p>
      )}
    </div>
  );
});

export default TextField;
